import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import useAuthStore from '~/stores/auth/useAuthStore';
import { useTokenStore } from '~/stores/token/useTokenStore';

type IRetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

type IAuthInterceptorConfig = {
  onUnauthorized?: () => void;
  tokenEndpoint?: string;
};

export function setupAuthInterceptor(instance: AxiosInstance, config: IAuthInterceptorConfig = {}) {
  const tokenEndpoint = config.tokenEndpoint ?? '/connect/token';

  instance.interceptors.response.use(
    res => res,
    async (error: AxiosError) => {
      const originalRequest = error.config as IRetryableRequest | undefined;
      const isTokenEndpoint = originalRequest?.url?.includes(tokenEndpoint);

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isTokenEndpoint
      ) {
        originalRequest._retry = true;

        try {
          const newAccessToken = await useAuthStore.getState().authTokenSlice.forceRefresh();

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          config.onUnauthorized?.();
          useTokenStore.clearAll();
          await useAuthStore.getState().authTokenSlice.logout();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );
}
